import {
  CurlRequest,
  HttpMethod,
  isHttpMethod,
} from "@/v2/entities/curl-request";
import { unwrapMangledJsonBody } from "./CurlRequest.utils";
import { useMemo } from "react";

function stripQuotes(s: string): string {
  if (!s) return s;
  const q = s[0];
  if ((q === "'" || q === '"') && s[s.length - 1] === q) return s.slice(1, -1);
  return s;
}

function tokenizeCurl(raw: string): string[] {
  // 1) remove backslash-newline continuations
  const joined = raw.replace(/\\\r?\n/g, " ").trim();

  // 2) tokenize respecting quotes
  const tokens: string[] = [];
  let curr = "";
  let inSingle = false;
  let inDouble = false;
  let escaped = false;

  for (let i = 0; i < joined.length; i++) {
    const ch = joined[i];

    if (escaped) {
      curr += ch;
      escaped = false;
      continue;
    }
    if (ch === "\\") {
      escaped = true;
      continue;
    }
    if (ch === "'" && !inDouble) {
      inSingle = !inSingle;
      curr += ch;
      continue;
    }
    if (ch === '"' && !inSingle) {
      inDouble = !inDouble;
      curr += ch;
      continue;
    }

    if (!inSingle && !inDouble && /\s/.test(ch)) {
      if (curr) {
        tokens.push(curr);
        curr = "";
      }
    } else {
      curr += ch;
    }
  }
  if (curr) tokens.push(curr);

  return tokens;
}

function parseQueryParams(url: string): Record<string, string> {
  const out: Record<string, string> = {};
  const qIndex = url.indexOf("?");
  if (qIndex === -1) return out;
  const query = url.slice(qIndex + 1);
  for (const pair of query.split("&")) {
    if (!pair) continue;
    const eq = pair.indexOf("=");
    if (eq === -1) {
      out[decodeURIComponent(pair)] = "";
    } else {
      const k = pair.slice(0, eq);
      const v = pair.slice(eq + 1);
      out[decodeURIComponent(k)] = decodeURIComponent(v);
    }
  }
  return out;
}

function parsePathParams(url: string): {
  pathParams: Record<string, string>;
  cleanUrl: string;
} {
  const pathParams: Record<string, string> = {};
  // Updated regex to match both ${paramName} and {paramName} patterns
  const pathParamRegex = /\$?\{([^}]+)\}/g;
  let match;
  let cleanUrl = url;

  while ((match = pathParamRegex.exec(url)) !== null) {
    const paramName = match[1];
    pathParams[paramName] = ""; // Initialize with empty value
    // Replace the parameter placeholder with a placeholder for the clean URL
    cleanUrl = cleanUrl.replace(match[0], `{${paramName}}`);
  }

  return { pathParams, cleanUrl };
}

function parseBody(chunks: string[]): Record<string, any> {
  if (chunks.length === 0) return {};

  // If we have multiple chunks, join them with spaces (not &)
  // This handles cases where JSON is split across multiple -d flags
  const bodyRaw = chunks.map(stripQuotes).join(" ");

  // Try JSON first. Also recover bodies Skill Lab saved as `${{...}}`.
  const trimmed = unwrapMangledJsonBody(bodyRaw);
  if (
    (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
    (trimmed.startsWith("[") && trimmed.endsWith("]"))
  ) {
    try {
      return JSON.parse(trimmed);
    } catch {
      // fall through to form logic
    }
  }

  // Form-encoded a=b&c=d style
  // For form data, we need to join with & and split properly
  const formData = chunks.map(stripQuotes).join("&");
  const out: Record<string, any> = {};
  for (const pair of formData.split("&")) {
    if (!pair) continue;
    const eq = pair.indexOf("=");
    if (eq === -1) {
      out[decodeURIComponent(pair)] = "";
    } else {
      const k = pair.slice(0, eq);
      const v = pair.slice(eq + 1);
      out[decodeURIComponent(k)] = decodeURIComponent(v);
    }
  }
  return out;
}

function parseCurl(curl: string): CurlRequest {
  const tokens = tokenizeCurl(curl);

  // Find URL and method
  let method: HttpMethod = "GET";
  let url = "";
  const headers: Record<string, string> = {};
  const dataChunks: string[] = [];
  let auth: CurlRequest["auth"];
  let options: CurlRequest["options"];

  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];

    if (t === "curl") continue;

    // Method
    if ((t === "-X" || t === "--request") && tokens[i + 1]) {
      const tryMethod = tokens[++i].toUpperCase();
      if (isHttpMethod(tryMethod)) {
        method = tryMethod;
        continue;
      }
    }

    // URL
    if (t === "--url" && tokens[i + 1]) {
      url = stripQuotes(tokens[++i]);
      continue;
    }
    if (!t.startsWith("-") && !url) {
      // First non-flag token after "curl" is often the URL
      url = stripQuotes(t);
      continue;
    }

    // Headers
    if ((t === "-H" || t === "--header") && tokens[i + 1]) {
      const raw = stripQuotes(tokens[++i]); // e.g., 'Content-Type: application/json'
      const colonIdx = raw.indexOf(":");
      if (colonIdx !== -1) {
        const name = raw.slice(0, colonIdx).trim();
        const value = raw.slice(colonIdx + 1).trim();

        if (/^authorization$/i.test(name)) {
          // Parse Authorization header into CurlRequest auth format
          if (value.toLowerCase().startsWith("basic ")) {
            try {
              const base64Credentials = value.substring(6);
              const credentials = atob(base64Credentials);
              const [user, password] = credentials.split(":", 2);
              auth = {
                type: "basic",
                user: user || "",
                password: password || "",
              };
            } catch (e) {
              // If base64 decoding fails, store as raw header
              headers[name] = value;
            }
          } else if (value.toLowerCase().startsWith("bearer ")) {
            const token = value.substring(7);
            auth = {
              type: "bearer",
              token: token,
            };
          } else {
            // Other authorization types, store as header
            headers[name] = value;
          }
        } else if (
          /^x-api-key$/i.test(name) ||
          /^api-key$/i.test(name) ||
          /^apikey$/i.test(name)
        ) {
          // For API key, create API key auth config
          auth = {
            type: "apikey",
            key: name,
            value: value,
            location: "header",
          };
        } else {
          headers[name] = value;
        }
      }
      continue;
    }

    // Data
    if (
      t === "-d" ||
      t === "--data" ||
      t === "--data-raw" ||
      t === "--data-binary" ||
      t === "--data-urlencode"
    ) {
      if (tokens[i + 1]) {
        dataChunks.push(tokens[++i]);
        // If method not explicitly set, curl uses POST when data is present
        if (method === "GET") method = "POST";
      }
      continue;
    }

    // Basic auth
    if ((t === "-u" || t === "--user") && tokens[i + 1]) {
      const creds = stripQuotes(tokens[++i]); // user:pass
      const [user, password] = creds.split(":", 2);
      auth = {
        type: "basic",
        user: user || "",
        password: password || "",
      };
      continue;
    }

    // Options
    if (t === "-L" || t === "--location") {
      options = { ...options, followRedirects: true };
      continue;
    }
    if (t === "-k" || t === "--insecure") {
      options = { ...options, insecure: true };
      continue;
    }
    if (t === "--compressed") {
      options = { ...options, compressed: true };
      continue;
    }
    if (t === "--max-time" && tokens[i + 1]) {
      const timeout = parseInt(tokens[++i], 10);
      if (!isNaN(timeout)) {
        options = { ...options, timeoutSeconds: timeout };
      }
      continue;
    }
    if (t === "-o" && tokens[i + 1]) {
      options = { ...options, outputFile: stripQuotes(tokens[++i]) };
      continue;
    }
  }

  // Fallback: if method still not set and no data -> keep GET
  const queryParams = parseQueryParams(url);
  
  // Convert ${paramName} back to {paramName} for the UI
  const normalizedUrl = url.replace(/\$\{([^}]+)\}/g, '{$1}');
  
  const { pathParams, cleanUrl } = parsePathParams(normalizedUrl);
  const bodyParams = parseBody(dataChunks);

  return {
    method,
    url: cleanUrl,
    queryParams,
    pathParams: Object.keys(pathParams).length ? pathParams : undefined,
    headers: Object.keys(headers).length ? headers : undefined,
    body: Object.keys(bodyParams).length ? bodyParams : undefined,
    auth,
    options: Object.keys(options || {}).length ? options : undefined,
  };
}

function buildCurl(request: CurlRequest, options: { substitutePathParams?: boolean } = { substitutePathParams: true }): string {
  const parts: string[] = ["curl"];

  // Add method
  if (request.method && request.method !== "GET") {
    parts.push("-X", request.method);
  }

  // Add URL with path and query parameters
  let url = request.url;

  // Substitute path parameters only if requested (for actual requests)
  // For skill templates, we want to keep the placeholders
  if (options.substitutePathParams && request.pathParams && Object.keys(request.pathParams).length > 0) {
    Object.entries(request.pathParams).forEach(([key, value]) => {
      url = url.replace(`{${key}}`, encodeURIComponent(value));
    });
  }

  // Add query parameters
  if (request.queryParams && Object.keys(request.queryParams).length > 0) {
    const urlObj = new URL(url, "http://example.com");
    Object.entries(request.queryParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => urlObj.searchParams.append(key, v));
      } else {
        urlObj.searchParams.set(key, value);
      }
    });
    url = urlObj.toString();
  }
  parts.push(`"${url}"`);

  // Add headers
  if (request.headers) {
    Object.entries(request.headers).forEach(([key, value]) => {
      parts.push("-H", `"${key}: ${value}"`);
    });
  }

  // Add authentication
  if (request.auth) {
    switch (request.auth.type) {
      case "basic":
        parts.push("-u", `"${request.auth.user}:${request.auth.password}"`);
        break;
      case "bearer":
        parts.push("-H", `"Authorization: Bearer ${request.auth.token}"`);
        break;
      case "apikey":
        if (request.auth.location === "header") {
          parts.push("-H", `"${request.auth.key}: ${request.auth.value}"`);
        } else if (request.auth.location === "query") {
          // Add to URL query params
          const urlObj = new URL(url);
          urlObj.searchParams.set(request.auth.key, request.auth.value);
          // Replace the URL in parts
          const urlIndex = parts.findIndex(
            (part) => part.startsWith('"') && part.endsWith('"')
          );
          if (urlIndex !== -1) {
            parts[urlIndex] = `"${urlObj.toString()}"`;
          }
        }
        break;
    }
  }

  // Add body data
  if (request.body) {
    let bodyData: string;
    if (typeof request.body === "string") {
      bodyData = request.body;
    } else {
      bodyData = JSON.stringify(request.body);
    }
    parts.push("--data-raw", `'${bodyData}'`);
  }

  // Add options
  if (request.options) {
    if (request.options.followRedirects) {
      parts.push("-L");
    }
    if (request.options.insecure) {
      parts.push("-k");
    }
    if (request.options.compressed) {
      parts.push("--compressed");
    }
    if (request.options.timeoutSeconds) {
      parts.push("--max-time", request.options.timeoutSeconds.toString());
    }
    if (request.options.outputFile) {
      parts.push("-o", `"${request.options.outputFile}"`);
    }
  }

  return parts.join(" ");
}

export function useCurlRequestTransformer() {
  return useMemo(() => {
    return {
      parseCurl,
      buildCurl: (request: CurlRequest, options?: { substitutePathParams?: boolean }) => buildCurl(request, options),
      parsePathParams,
    };
  }, []);
}
